import React, { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ClienteCombobox from "@/components/ClienteCombobox";
import { Sparkles, Loader2, Mail, Table2 } from "lucide-react";
import { toast } from "sonner";
import { tablasDesdeHtml, esPerfilDecorGalva } from "@/lib/outlookPaste";

/** Crear petición desde email (V1 manual): cliente OBLIGATORIO y elegido por el usuario (el perfil sale del cliente,
 *  nunca del texto), asunto obligatorio (nombre de la petición), cuerpo pegado desde Outlook (captura tablas del HTML). */
export default function EmailToPeticionDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [tablas, setTablas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || clientes.length) return;
    api.get("/clientes").then((r) => setClientes((r.data || []).filter((c) => c.estado !== "inactivo"))).catch(() => toast.error("No se pudieron cargar los clientes"));
  }, [open, clientes.length]);

  const decor = useMemo(() => clientes.find(esPerfilDecorGalva), [clientes]);
  const perfilDecor = esPerfilDecorGalva(cliente);
  const puedeAnalizar = !!cliente && subject.trim().length > 0 && body.trim().length > 0 && !loading;

  const onPaste = (e) => {
    const html = e.clipboardData?.getData("text/html");
    const t = tablasDesdeHtml(html);
    if (t.length) {
      setTablas(t);
      toast.success(`${t.length === 1 ? `Tabla detectada: ${t[0].n_filas} filas` : `${t.length} tablas detectadas`}`);
    }
  };

  const reset = () => { setSubject(""); setBody(""); setTablas([]); setCliente(null); };

  const submit = async () => {
    if (!puedeAnalizar) return;
    setLoading(true);
    try {
      const { data } = await api.post("/peticiones-ia/parse-email", {
        cliente_id: cliente.id, email_subject: subject.trim(), email_raw: body, tablas,
      });
      toast.success(`✅ Borrador IA creado · ${perfilDecor ? "perfil Decor Galvá" : "perfil genérico"}`);
      setOpen(false); reset();
      onCreated?.(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Error procesando email";
      toast.error(String(detail).slice(0, 240));
    } finally { setLoading(false); }
  };

  const nFilas = tablas.reduce((a, t) => a + (t.n_filas || 0), 0);

  return (
    <>
      <Button onClick={() => setOpen(true)} variant="outline"
        className="bg-transparent border-violet-500/50 text-violet-200 hover:bg-violet-500/10" data-testid="btn-email-to-peticion">
        <Sparkles className="w-4 h-4 mr-2" /> Crear desde email
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-violet-400" /> Crear petición desde email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-violet-500/5 border border-violet-500/20 rounded-lg p-3 text-xs text-violet-200" data-testid="email-ayuda">
              💡 Selecciona el cliente, copia el asunto y pega el cuerpo tal como Outlook te permita. No hace falta copiar remitente, destinatarios ni CC.
            </div>

            <div>
              <Label className="text-xs text-zinc-400">Cliente *</Label>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 min-w-0">
                  <ClienteCombobox
                    value={cliente?.id || ""}
                    onChange={(id) => setCliente(clientes.find((c) => c.id === id) || null)}
                    clientes={clientes}
                    testId="email-cliente-combobox"
                    className="h-9"
                    getBadge={(c) => esPerfilDecorGalva(c)
                      ? <span className="text-[9px] px-1 rounded bg-amber-500/20 text-amber-200 shrink-0">perfil</span> : null}
                  />
                </div>
                {decor && cliente?.id !== decor.id && (
                  <button type="button" onClick={() => setCliente(decor)} data-testid="email-cliente-chip-decor"
                    className="text-[11px] px-2.5 py-1.5 rounded-full border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 whitespace-nowrap">
                    Decor Galvá
                  </button>
                )}
              </div>
              <div className={`mt-1.5 text-[11px] px-2 py-1 rounded border ${!cliente ? "border-zinc-800 text-zinc-500" : perfilDecor ? "border-amber-500/40 bg-amber-500/10 text-amber-100 font-semibold" : "border-sky-500/30 bg-sky-500/5 text-sky-200"}`}
                data-testid="email-perfil-indicador">
                {!cliente ? "Perfil: — (elige cliente)" : perfilDecor
                  ? "✔ Perfil Decor Galvá activo · reglas D/V/TEXTIL RETRO, material aportado, AAFF y descripción automática"
                  : `Perfil genérico para ${cliente.nombre} · sin reglas Decor Galvá; la IA propone y tú decides producción/origen`}
              </div>
            </div>

            <div>
              <Label className="text-xs text-zinc-400">Asunto * <span className="text-zinc-600">(será el nombre de la petición)</span></Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="YSL / Prada / Armani Druni 02-03/11/26"
                className="bg-zinc-900 border-zinc-800 mt-1" data-testid="email-subject-input" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Cuerpo del email *</Label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} onPaste={onPaste} rows={12}
                placeholder="Buenos días Dani. Tenemos que instalar el …&#10;(pega aquí el correo con su tabla)"
                className="bg-zinc-900 border-zinc-800 mt-1 font-mono text-xs" data-testid="email-body-input" />
              <div className="flex items-center justify-between text-[10px] mt-1">
                <span className="text-zinc-500">{body.length.toLocaleString()} caracteres</span>
                {tablas.length > 0 ? (
                  <span className="flex items-center gap-1 text-emerald-300" data-testid="email-tablas-indicador">
                    <Table2 className="w-3 h-3" /> {tablas.length === 1 ? `Tabla detectada: ${nFilas} filas` : `${tablas.length} tablas detectadas (${nFilas} filas)`}
                    <button type="button" className="underline text-zinc-500 ml-1" onClick={() => setTablas([])}>quitar</button>
                  </span>
                ) : body.trim() ? <span className="text-zinc-600" data-testid="email-tablas-indicador">Sin tabla HTML: la IA reconstruirá las filas desde el texto</span> : null}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="bg-transparent border-zinc-700" disabled={loading}>Cancelar</Button>
            <Button onClick={submit} disabled={!puedeAnalizar} className="bg-violet-500 hover:bg-violet-400 text-zinc-950 font-semibold" data-testid="btn-parse-email"
              title={!cliente ? "Elige el cliente" : !subject.trim() ? "Falta el asunto" : !body.trim() ? "Pega el cuerpo" : ""}>
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizando con IA…</> : <><Sparkles className="w-4 h-4 mr-2" /> Analizar y pre-crear</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
